import { useState, useEffect, useCallback } from 'react';
import { SiSalesforce } from 'react-icons/si';
import { supabase } from '../supabaseClient';
import '../styles/DashboardSalesforce.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function DashboardSalesforce({ user }) {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [syncMode, setSyncMode] = useState('manual'); // 'auto' or 'manual'
  const [salesforceUsername, setSalesforceUsername] = useState('');
  const [error, setError] = useState('');

  const checkConnectionStatus = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Get user's Salesforce settings from database
      const { data, error } = await supabase
        .from('Users')
        .select('salesforce_connected, salesforce_sync_mode, salesforce_username')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      if (data) {
        setIsConnected(data.salesforce_connected || false);
        setSyncMode(data.salesforce_sync_mode || 'manual');
        setSalesforceUsername(data.salesforce_username || '');
      }
    } catch (err) {
      console.error('Error checking Salesforce status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Check connection status and sync mode on mount
  useEffect(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError('');

    try {
      // Get the auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Call backend to get OAuth URL
      const response = await fetch(`${BACKEND_URL}/salesforce/auth-url`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get authorization URL');
      }

      const { auth_url } = await response.json();

      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        auth_url,
        'salesforce-oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Poll for popup close and check connection status
      const pollInterval = setInterval(async () => {
        if (popup.closed) {
          clearInterval(pollInterval);
          setIsConnecting(false);
          // Check if connection was successful
          await checkConnectionStatus();
        }
      }, 500);
    } catch (err) {
      console.error('Error connecting to Salesforce:', err);
      setError(err.message);
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Call backend to revoke tokens and clear settings
      const response = await fetch(`${BACKEND_URL}/salesforce/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to disconnect');
      }

      setIsConnected(false);
      setSalesforceUsername('');
      setSyncMode('manual');
    } catch (err) {
      console.error('Error disconnecting from Salesforce:', err);
      setError(err.message);
    }
  };

  const handleSyncModeChange = async (newMode) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Update sync mode in backend
      const response = await fetch(`${BACKEND_URL}/salesforce/sync-mode`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sync_mode: newMode })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update sync mode');
      }

      setSyncMode(newMode);
    } catch (err) {
      console.error('Error updating sync mode:', err);
      setError(err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-salesforce">
        <div className="salesforce-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-salesforce">
      <div className="salesforce-header">
        <h1 className="salesforce-title">
          <SiSalesforce className="salesforce-title-icon" />
          Salesforce Integration
        </h1>
      </div>

      {error && (
        <div className="salesforce-error">
          {error}
          <button onClick={() => setError('')} className="salesforce-error-close">&times;</button>
        </div>
      )}

      <div className="salesforce-content">
        {!isConnected ? (
          <div className="salesforce-connect-section">
            <div
              className="salesforce-connect-card"
              onClick={!isConnecting ? handleConnect : undefined}
            >
              <SiSalesforce className="salesforce-connect-icon" />
              <h2>Click here to connect to your Salesforce account</h2>
              <p>Connect your Salesforce account to automatically sync leads as opportunities.</p>
              {isConnecting && <div className="salesforce-connecting">Connecting...</div>}
            </div>
          </div>
        ) : (
          <div className="salesforce-connected-section">
            <div className="salesforce-status-card">
              <div className="salesforce-status-header">
                <div className="salesforce-status-info">
                  <span className="salesforce-status-indicator connected"></span>
                  <span className="salesforce-status-text">Connected</span>
                </div>
                {salesforceUsername && (
                  <span className="salesforce-username">{salesforceUsername}</span>
                )}
              </div>
              <button
                className="salesforce-disconnect-btn"
                onClick={handleDisconnect}
              >
                Disconnect
              </button>
            </div>

            <div className="salesforce-sync-options">
              <h3>Sync Options</h3>
              <div className="salesforce-radio-group">
                <label className={`salesforce-radio-option ${syncMode === 'auto' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="syncMode"
                    value="auto"
                    checked={syncMode === 'auto'}
                    onChange={() => handleSyncModeChange('auto')}
                  />
                  <span className="radio-custom"></span>
                  <div className="radio-content">
                    <span className="radio-label">Sync all new leads with Salesforce</span>
                    <span className="radio-description">Automatically add all new leads to your Opportunities and check for duplicates.</span>
                  </div>
                </label>

                <label className={`salesforce-radio-option ${syncMode === 'manual' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="syncMode"
                    value="manual"
                    checked={syncMode === 'manual'}
                    onChange={() => handleSyncModeChange('manual')}
                  />
                  <span className="radio-custom"></span>
                  <div className="radio-content">
                    <span className="radio-label">Manually choose leads to add</span>
                    <span className="radio-description">Select individual leads to sync.</span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardSalesforce;
